import { useState } from 'react';

import { useQueryClient } from '@tanstack/react-query';
import PropTypes from 'prop-types';
import { BsArrowRepeat, BsMicrosoft, BsServer, BsTrash } from 'react-icons/bs';

import Alert from '#globalComponents/Alert/Alert';
import IconButton from '#globalComponents/IconButton/IconButton';
import ConfirmModal from '#globalComponents/Overlay/ConfirmModal';
import Tag from '#globalComponents/Tag/Tag';
import { formatDateWithTime } from '#lib/dateUtils';
import { testEmailAccountConnection } from '#settingsFeature/api/emailAccountsService';
import { emailAccountsKeys } from '#settingsFeature/models/emailAccounts/keys';

const MICROSOFT_PROVIDERS = ['outlook', 'office365'];

const SpinningArrowRepeat = ({ className }) => <BsArrowRepeat className={`${className} animate-spin`} />;

SpinningArrowRepeat.propTypes = {
	className: PropTypes.string,
};

const ConnectionStatusBadge = ({ connectionStatus, lastConnectionTest, lastConnectionError }) => {
	if (connectionStatus === 'connected') {
		return (
			<span title={lastConnectionTest ? `Getestet: ${formatDateWithTime(lastConnectionTest)}` : undefined}>
				<Tag tone="success" label="Verbunden" />
			</span>
		);
	}
	if (connectionStatus === 'failed') {
		return (
			<span className="cursor-help" title={lastConnectionError || 'Verbindungstest fehlgeschlagen'}>
				<Tag tone="warning" label="Fehler" />
			</span>
		);
	}
	return <Tag tone="neutral" label="Nicht getestet" />;
};

ConnectionStatusBadge.propTypes = {
	connectionStatus: PropTypes.string,
	lastConnectionTest: PropTypes.string,
	lastConnectionError: PropTypes.string,
};

const EmailAccountListItem = ({ account, onClick, onDelete, onConnectionTest }) => {
	const queryClient = useQueryClient();

	const ProviderIcon = MICROSOFT_PROVIDERS.includes(account.provider?.toLowerCase()) ? BsMicrosoft : BsServer;
	const [isTesting, setIsTesting] = useState(false);
	const [testResult, setTestResult] = useState(null);
	const [showDeleteModal, setShowDeleteModal] = useState(false);
	const [isDeleting, setIsDeleting] = useState(false);

	const handleDelete = (e) => {
		e.stopPropagation();
		setShowDeleteModal(true);
	};

	const handleConfirmDelete = async () => {
		setIsDeleting(true);
		try {
			await onDelete(account.id);
			setShowDeleteModal(false);
		} catch {
			// ignored
		} finally {
			setIsDeleting(false);
		}
	};

	const handleCancelDelete = () => {
		setShowDeleteModal(false);
	};

	const handleTestConnection = async (e) => {
		e.stopPropagation();
		setIsTesting(true);
		setTestResult(null);

		try {
			const result = await testEmailAccountConnection(account.id);
			setTestResult(result);

			await queryClient.invalidateQueries({ queryKey: emailAccountsKeys.all });
			onConnectionTest?.(account.id, result);
			setTimeout(() => setTestResult(null), 3000);
		} catch (error) {
			setTestResult({
				success: false,
				message: error.message || 'Verbindungstest fehlgeschlagen',
				status: 'failed',
			});
			setTimeout(() => setTestResult(null), 5000);
		} finally {
			setIsTesting(false);
		}
	};

	return (
		<>
			<div
				onClick={() => onClick(account)}
				className="flex cursor-pointer items-center justify-between gap-4 rounded-card border border-bgBase-500 p-4 transition-colors hover:bg-bgBase-300"
			>
				<div className="flex min-w-0 items-center gap-3">
					<div className="flex h-9 w-9 flex-shrink-0 items-center justify-center rounded-control bg-primary-100 text-primary-600">
						<ProviderIcon className="h-4 w-4" />
					</div>

					<div className="min-w-0 flex-1">
						<div className="mb-0.5 flex items-center gap-2">
							<p className="truncate text-sm font-semibold text-text">
								{account.senderName || account.emailAddress}
							</p>
							{account.provider && <Tag tone="neutral" label={account.provider} />}
							{account.authStrategy && (
								<Tag tone="primary" label={account.authStrategy === 'oauth2' ? 'OAuth2' : 'SMTP'} />
							)}
						</div>
						{account.senderName && (
							<p className="truncate text-xs text-bgBase-800">{account.emailAddress}</p>
						)}
						{account.smtpHost && (
							<p className="truncate text-xs text-bgBase-800">
								{account.smtpHost}
								{account.smtpPort && `:${account.smtpPort}`}
								{account.smtpUseTls && ' (TLS)'}
							</p>
						)}
					</div>
				</div>

				<div className="flex flex-shrink-0 items-center gap-2">
					<ConnectionStatusBadge
						connectionStatus={account.connectionStatus || 'untested'}
						lastConnectionTest={account.lastConnectionTest}
						lastConnectionError={account.lastConnectionError}
					/>
					<IconButton
						icon={isTesting ? SpinningArrowRepeat : BsArrowRepeat}
						label="Verbindung testen"
						tone="primary"
						onClick={handleTestConnection}
						disabled={isTesting}
					/>
					<IconButton icon={BsTrash} label="Konto löschen" tone="danger" onClick={handleDelete} />
				</div>
			</div>

			{testResult && <Alert tone={testResult.success ? 'success' : 'warning'}>{testResult.message}</Alert>}

			<ConfirmModal
				isOpen={showDeleteModal}
				onClose={handleCancelDelete}
				onConfirm={handleConfirmDelete}
				title="E-Mail-Konto löschen"
				message={`Möchten Sie das E-Mail-Konto "${account.emailAddress}" wirklich löschen? Alle geplanten Mails des Accounts werden entfernt. Die Aktion kann nicht rückgängig gemacht werden`}
				confirmText="Konto löschen"
				cancelText="Abbrechen"
				isLoading={isDeleting}
			/>
		</>
	);
};

EmailAccountListItem.propTypes = {
	account: PropTypes.shape({
		id: PropTypes.string.isRequired,
		emailAddress: PropTypes.string,
		senderName: PropTypes.string,
		smtpHost: PropTypes.string,
		smtpPort: PropTypes.number,
		smtpUsername: PropTypes.string,
		smtpUseTls: PropTypes.bool,
		authStrategy: PropTypes.string,
		provider: PropTypes.string,
		connectionStatus: PropTypes.string,
		lastConnectionTest: PropTypes.string,
		lastConnectionError: PropTypes.string,
		createdAt: PropTypes.string,
		updatedAt: PropTypes.string,
	}).isRequired,
	onClick: PropTypes.func.isRequired,
	onDelete: PropTypes.func.isRequired,
	onConnectionTest: PropTypes.func,
};

export default EmailAccountListItem;
